import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';

import { NotificationsService } from './notifications.service';
import { NotificationDto } from './notifications.models';
import { PmRealtimeService, PmNotificationEvent } from '../../core/services/pm-realtime.service';

@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [
    CommonModule, RouterLink, MatIconModule, MatButtonModule,
    MatProgressSpinnerModule, MatDividerModule
  ],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.scss'
})
export class NotificationsComponent implements OnInit, OnDestroy {
  private readonly notifications = inject(NotificationsService);
  private readonly realtime = inject(PmRealtimeService);
  private readonly destroy$ = new Subject<void>();

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly items = signal<NotificationDto[]>([]);
  readonly toast = signal<PmNotificationEvent | null>(null);

  ngOnInit(): void {
    this.load();
    void this.realtime.ensureNotificationsConnected().catch(() => undefined);
    this.realtime.notificationCreated$.pipe(takeUntil(this.destroy$)).subscribe((n) => {
      this.toast.set(n);
      this.load();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.notifications.list({ page: 1, pageSize: 100 }).subscribe({
      next: (page) => {
        this.items.set(page.items ?? []);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load notifications.');
        this.items.set([]);
        this.loading.set(false);
      }
    });
  }

  markRead(item: NotificationDto): void {
    if (item.readAt) return;
    this.notifications.markRead(item.id).subscribe({
      next: () => this.items.update(list => list.map(n => n.id === item.id ? { ...n, readAt: new Date().toISOString() } : n)),
      error: () => undefined
    });
  }

  markAllRead(): void {
    this.notifications.markAllRead().subscribe({
      next: () => this.items.update(list => list.map(n => ({ ...n, readAt: n.readAt ?? new Date().toISOString() }))),
      error: () => undefined
    });
  }

  isUnread(item: NotificationDto): boolean {
    return !item.readAt;
  }

  timeLabel(value: string): string {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString();
  }
}
